import * as Yup from "yup"
import { Ref, forwardRef } from "react"
import { Form, Formik, FormikProps } from "formik"
import { AgreementOptions, BusinessType, EntityHistory, InvoiceType } from "../../supportEntitiesTypes"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { SupplierCreateUpdateContent } from "./SupplierCreateUpdateContent"
import { SupplierFormValues } from "../supplierTypes"

type CreateOrEditSupplierFormProps = Readonly<{
    initialValues?: SupplierFormValues | null
    history?: EntityHistory
    onSubmit: (formData: SupplierFormValues) => void
}>

const defaultInitialValues: SupplierFormValues = {
    businessType: "",
    address1: "",
    address2: "",
    city: "",
    name: "",
    businessNumber: "",
    contactPerson: "",
    email: "",
    countryCode: "",
    isSystemOwner: "",
    postalCode: "",
    telephone: "",
    isActive: true,
    eanNumber: "",
    invoiceType: "",
    attention: "",
    debitorNo: "",
    invoiceEmail: "",
    reminderEmail: "",
    poNumber: "",
}

type FormikRef = Ref<FormikProps<SupplierFormValues>>

export const SupplierCreateUpdateForm = forwardRef(
    ({ initialValues, onSubmit, history }: CreateOrEditSupplierFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const supplierSchema = Yup.object().shape({
            isActive: Yup.boolean(),
            businessType: Yup.string()
                .oneOf(
                    Object.values(BusinessType),
                    l10n.getString("support-create-entity-error-label-company-invalid-type"),
                )
                .required()
                .label(l10n.getString("support-create-entity-error-label-company-type")),
            isSystemOwner: Yup.string()
                .required()
                .label(l10n.getString("support-create-entity-error-label-system-owner")),
            businessNumber: Yup.string()
                .required()
                .label(l10n.getString("support-create-entity-error-label-company-number")),
            name: Yup.string().required().label(l10n.getString("support-create-entity-error-label-company-name")),
            countryCode: Yup.string().required().label(l10n.getString("support-create-entity-error-label-country")),
            address1: Yup.string().required().label(l10n.getString("support-create-entity-error-label-address")),
            address2: Yup.string().label(l10n.getString("support-create-entity-error-label-address")),
            postalCode: Yup.string().required().label(l10n.getString("support-create-entity-error-label-postal-code")),
            city: Yup.string().required().label(l10n.getString("support-create-entity-error-label-city")),
            contactPerson: Yup.string()
                .required()
                .label(l10n.getString("support-create-entity-error-label-contact-person")),
            email: Yup.string().email().required().label(l10n.getString("support-create-entity-error-label-email")),
            telephone: Yup.string().required().label(l10n.getString("support-create-entity-error-label-telephone")),
            invoiceType: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-invoice-type"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            eanNumber: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-ean-number"))
                .when("invoiceType", {
                    is: (val: InvoiceType) => val === InvoiceType.EAN_NUMBER,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            invoiceEmail: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-invoice-email"))
                .when("invoiceType", {
                    is: (val: InvoiceType) => val === InvoiceType.EMAIL,
                    then: (schema) => schema.required().email(),
                    otherwise: (schema) => schema.optional(),
                }),
            attention: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-attention"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            debitorNo: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-debitot-no"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            reminderEmail: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-reminder-email"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required().email(),
                    otherwise: (schema) => schema.optional(),
                }),
            poNumber: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-po-number"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            implementationDeadline: Yup.string().when("isSystemOwner", {
                is: (val: AgreementOptions) => val === AgreementOptions.YES,
                then: (schema) => schema.required(l10n.getString("required-field")),
                otherwise: (schema) => schema.optional(),
            }),
        })

        function handleSubmit(values: SupplierFormValues) {
            onSubmit(values)
        }

        return (
            <Formik<SupplierFormValues>
                enableReinitialize
                initialValues={initialValues ? initialValues : defaultInitialValues}
                validationSchema={supplierSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                <Form>
                    <SupplierCreateUpdateContent history={history} />
                </Form>
            </Formik>
        )
    },
)
